import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, ArrowLeft, Home, Lock } from 'lucide-react'
import { Button, BackgroundSlider, ErrorBanner } from '@/components'
import type { ErrorBannerTone } from '@/components'
import { Link, useNavigate } from 'react-router-dom'
import { authService } from '@/services/authService'
import { useLanguage } from '@/contexts/LanguageContext'
import { consumeAuthReason } from '@/services/api'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'

const LoginScreen: React.FC = () => {
  const navigate = useNavigate()
  const { t, language } = useLanguage()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Oturumun DUSMESI bir kullanici hatasi degil, bir bilgilendirmedir: sari.
  const [errorTone, setErrorTone] = useState<ErrorBannerTone>('error')

  // 401 sonrasi sert yonlendirme (window.location.href = '/login') React state'ini
  // sifirladigi icin sebep sessionStorage uzerinden tasiniyor. Mount'ta bir kez
  // okunur ve tuketilir; aksi halde kullanici neden atildigini hic ogrenemiyordu.
  useEffect(() => {
    const reason = consumeAuthReason()
    if (reason) {
      setError(reason)
      setErrorTone('warning')
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return

    setIsLoading(true)
    setError(null)
    setErrorTone('error')

    try {
      const response = await authService.login(formData)
      
      if (response.success) {
        // Login başarılı - dashboard'a yönlendir
        navigate('/dashboard')
      } else {
        // Login başarısız - backend'in mesajı korunur, jenerik metinle EZİLMEZ.
        setError(responseErrorMessage(response, t, t.auth.login.loginError))
      }
    } catch (error) {
      // authService.login normalde throw etmez (hatayı {success:false} olarak döner),
      // ama beklenmeyen bir istisna olursa artık gerçek sebebi gösteriyoruz --
      // eskiden her istisna "Ağ bağlantı hatası" diye etiketleniyordu.
      logError('LoginScreen.handleSubmit', error)
      setError(toUserMessage(error, t, t.auth.login.loginError))
    } finally {
      setIsLoading(false)
    }
  }



  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Slider */}
      <div className="absolute inset-0">
        <BackgroundSlider showDots={false} interval={6500} />
      </div>
      
      {/* Transparent Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-blue-900/60 to-indigo-900/70" />
      
      {/* Additional subtle decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200/20 to-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
      </div>

      {/* Back to Home - Fixed position */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-4 left-4 z-50"
      >
        <Link 
          to="/" 
          className="flex items-center space-x-2 bg-white/90 backdrop-blur-md hover:bg-white text-secondary-700 hover:text-primary-600 px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
          <Home className="w-4 h-4" />
          <span className="text-sm font-medium hidden sm:inline">{t.nav.home}</span>
        </Link>
      </motion.div>

      <div className="flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-md w-full space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl border border-white/30 p-6 sm:p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              <ErrorBanner
                message={error}
                tone={errorTone}
                title={errorTone === 'warning' ? t.auth.login.securityNotice : t.auth.login.loginError}
                onDismiss={() => setError(null)}
              />

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-secondary-700 mb-3">
                  {t.auth.login.emailLabel}
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="block w-full pl-12 pr-4 py-3 sm:py-4 border border-secondary-200 rounded-xl leading-5 bg-white/70 placeholder-secondary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base"
                    placeholder={t.auth.login.emailPlaceholder}
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Mail className="h-5 w-5 text-secondary-400" />
                  </div>
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-secondary-700 mb-3">
                  {t.auth.login.passwordLabel}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="block w-full pl-12 pr-4 py-3 sm:py-4 border border-secondary-200 rounded-xl leading-5 bg-white/70 placeholder-secondary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base"
                    placeholder={t.auth.login.passwordPlaceholder}
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Lock className="h-5 w-5 text-secondary-400" />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <Button
                  type="submit"
                  loading={isLoading}
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
                  size="lg"
                >
                  {isLoading ? t.auth.login.loggingIn : t.auth.login.loginButton}
                </Button>
              </div>
            </form>

            {/* Additional Links */}
            <div className="mt-6 sm:mt-8 text-center">
              <Link to="/password-reset" className="text-primary-600 hover:text-primary-500 font-semibold hover:underline transition-all duration-200 text-sm">
                {t.auth.login.resetPassword}
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default LoginScreen
